import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

type Props = {
  portal: 'forkhire' | 'forkseek';
  currentPlan: string;
  pending: boolean;
  authoritativeDate: string | null;
  paidThroughDate: string | null;
  autoRenewEnabled: boolean;
  downgradeCleared: boolean;
  formatDate: (value: string | null) => string;
  cancelling: boolean;
  undoing: boolean;
  onCancel: () => void;
  onUndo: () => void;
};

export function SubscriptionCancellationManagement({
  portal,
  currentPlan,
  pending,
  authoritativeDate,
  paidThroughDate,
  autoRenewEnabled,
  downgradeCleared,
  formatDate,
  cancelling,
  undoing,
  onCancel,
  onUndo,
}: Props) {
  const { t } = useTranslation();
  const dateReady = Boolean(authoritativeDate && paidThroughDate);

  return (
    <Card
      data-testid={`${portal}-subscription-cancellation-management`}
      className="rounded-xl border-border"
    >
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base">{t('subscriptionCancellation.title')}</CardTitle>
          <Badge variant={pending ? 'secondary' : 'outline'}>
            {pending ? t('subscriptionCancellation.pending') : t('subscriptionCancellation.notPending')}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <p className="text-xs text-muted-foreground">{t('subscriptionCancellation.currentPlan')}</p>
            <p className="font-semibold text-foreground">{currentPlan}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">{t('subscriptionCancellation.accessEnd')}</p>
            <p className="font-semibold text-foreground">
              {dateReady ? formatDate(authoritativeDate) : t('subscriptionCancellation.unavailable')}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">{t('subscriptionCancellation.paidThrough')}</p>
            <p className="font-semibold text-foreground">
              {dateReady ? formatDate(paidThroughDate) : t('subscriptionCancellation.unavailable')}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">{t('subscriptionCancellation.autoRenew')}</p>
            <p className="font-semibold text-foreground">
              {autoRenewEnabled ? t('subscriptionCancellation.enabled') : t('subscriptionCancellation.disabled')}
            </p>
          </div>
        </div>

        {downgradeCleared && pending && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-50/50 p-3 text-sm dark:bg-amber-900/10">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <p>{t('subscriptionCancellation.downgradeCleared')}</p>
          </div>
        )}

        {!dateReady && (
          <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
            <p>{t('subscriptionCancellation.pricingUnavailable')}</p>
          </div>
        )}

        {pending ? (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" disabled={undoing || !dateReady}>
                <CheckCircle2 className="me-2 h-4 w-4" />
                {undoing ? t('subscriptionCancellation.undoing') : t('subscriptionCancellation.undo')}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('subscriptionCancellation.undoTitle')}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t('subscriptionCancellation.undoDescription', { date: formatDate(authoritativeDate) })}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('common.close')}</AlertDialogCancel>
                <AlertDialogAction onClick={onUndo}>{t('subscriptionCancellation.confirmUndo')}</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        ) : (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="text-destructive hover:text-destructive" disabled={cancelling || !dateReady}>
                <XCircle className="me-2 h-4 w-4" />
                {t('subscriptionCancellation.cancel')}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('subscriptionCancellation.cancelTitle')}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t('subscriptionCancellation.cancelDescription', { date: formatDate(authoritativeDate) })}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('subscriptionCancellation.keep')}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={onCancel}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {cancelling ? t('subscriptionCancellation.cancelling') : t('subscriptionCancellation.confirmCancel')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </CardContent>
    </Card>
  );
}
